import { useMemo, useState } from 'react';
import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { PreRegisterInfo } from '@/_features/motoboys/motoboy-list/types';
import { ModalShowDoc } from '@/_features/motoboys/approval-mat/components/modal-show-doc';
import { ENUM_STATUS_PRE_REGISTRATION } from '@/_features/motoboys/constants';
import styles from './styles.module.scss';

interface DocumentsProps {
  deliverymanBackRgPictureKey: string;
  deliverymanRgFrontPictureKey: string;
  deliverymanCnhPictureKey: string;
  deliverymanCrlvPictureKey: string;
  deliverymanPreRegistration: PreRegisterInfo;
}

interface InfoDocument {
  title: string;
  image: string | string[];
  approved: boolean;
}

function Documents({
  deliverymanBackRgPictureKey,
  deliverymanRgFrontPictureKey,
  deliverymanCnhPictureKey,
  deliverymanCrlvPictureKey,
  deliverymanPreRegistration,
}: DocumentsProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectInfoDocument, setSelectInfoDocument] = useState<
    Omit<InfoDocument, 'approved'>
  >({
    title: '',
    image: '',
  });

  function isUrl(url: string) {
    if (!url || typeof url !== 'string') return false;
    return url.includes('http');
  }

  const { status } = deliverymanPreRegistration;

  const handleClickImage = (infoDocument: Omit<InfoDocument, 'approved'>) => {
    const { image } = infoDocument;
    if (!image) return;

    const isArrayOfImages = Array.isArray(image);
    const isPdf = !isArrayOfImages && image.toLowerCase().includes('pdf');

    if (isPdf) {
      donwloadPdf(image);
    } else {
      showImage(infoDocument);
    }
  };

  const donwloadPdf = (imageUrl: string) => {
    window.open(imageUrl, '_blank');
  };

  const showImage = (infoDocument: Omit<InfoDocument, 'approved'>) => {
    setSelectInfoDocument(infoDocument);
    setShowModal(true);
  };

  const infos = useMemo(() => {
    const {
      hasApprovedPersonalData,
      hasApprovedBankAccount,
      hasApprovedCnh,
      hasApprovedCrlv,
      hasApprovedDeliverymanPicture,
      hasApprovedRg,
      hasApprovedSelfieWithRg,
      rg,
      cnh,
      crlv,
      deliverymanPictureId,
      deliverymanWithRgPictureId,
    } = deliverymanPreRegistration || {};

    const imgFrontRg = isUrl(deliverymanRgFrontPictureKey)
      ? deliverymanRgFrontPictureKey
      : rg?.imageBackId;

    const imgBackRg = isUrl(deliverymanBackRgPictureKey)
      ? deliverymanBackRgPictureKey
      : rg?.imageFrontId;

    const imgCnh = isUrl(deliverymanCnhPictureKey)
      ? deliverymanCnhPictureKey
      : cnh?.imageId;

    const imgCrlv = isUrl(deliverymanCrlvPictureKey)
      ? deliverymanCrlvPictureKey
      : crlv?.imageId;

    const imgSelfie = deliverymanPictureId;

    const imgSelfieWithDocument = deliverymanWithRgPictureId;

    const textReprovedDocuments: Partial<
      Record<ENUM_STATUS_PRE_REGISTRATION, string>
    > = {
      [ENUM_STATUS_PRE_REGISTRATION.REPROVED_FOR_CHECK]: '(reprovado)',
      [ENUM_STATUS_PRE_REGISTRATION.REPROVED_PEDING_RESEND_DOCUMENTATION]:
        '(informações ou documento reprovado)',
    };

    const infoArray = [
      {
        title: `Dados pessoais ${
          hasApprovedPersonalData ? '' : textReprovedDocuments[status] || ''
        }`,
        image: null,
        approved: !!hasApprovedPersonalData,
      },
      {
        title: `Dados bancários ${
          hasApprovedBankAccount ? '' : textReprovedDocuments[status] || ''
        }`,
        image: null,
        approved: !!hasApprovedBankAccount,
      },
      {
        title: `RG ${hasApprovedRg ? '' : textReprovedDocuments[status] || ''}`,
        image: imgFrontRg || imgBackRg ? [imgFrontRg, imgBackRg] : undefined,
        approved: !!hasApprovedRg,
      },
      {
        title: `CNH ${
          hasApprovedCnh ? '' : textReprovedDocuments[status] || ''
        }`,
        image: imgCnh,
        approved: !!hasApprovedCnh,
      },
      {
        title: `CRLV ${
          hasApprovedCrlv ? '' : textReprovedDocuments[status] || ''
        }`,
        image: imgCrlv,
        approved: !!hasApprovedCrlv,
      },
      {
        title: `Selfie (sem documento) ${
          hasApprovedSelfieWithRg ? '' : textReprovedDocuments[status] || ''
        }`,
        image: imgSelfie,
        approved: !!hasApprovedSelfieWithRg,
      },
      {
        title: `Selfie (com documento) ${
          hasApprovedDeliverymanPicture
            ? ''
            : textReprovedDocuments[status] || ''
        }`,
        image: imgSelfieWithDocument,
        approved: !!hasApprovedDeliverymanPicture,
      },
    ];

    return infoArray.filter(info => info.image !== undefined);
  }, [deliverymanPreRegistration]);

  const selectedStyle: Partial<Record<ENUM_STATUS_PRE_REGISTRATION, string>> = {
    [ENUM_STATUS_PRE_REGISTRATION.NEW_REGISTER_PEDING_VALIDATION]:
      styles.newDocument,
    [ENUM_STATUS_PRE_REGISTRATION.REPROVED_NEW_DOCUMENTATION_PROVIDED]:
      styles.newDocument,
    [ENUM_STATUS_PRE_REGISTRATION.REPROVED_PEDING_RESEND_DOCUMENTATION]:
      styles.reproved,
    [ENUM_STATUS_PRE_REGISTRATION.REPROVED_FOR_CHECK]: styles.fullReproved,
  };

  return (
    <div className={styles.container} data-testid="documents">
      {infos.map(info => (
        <div
          key={info.title}
          className={styles.documentsContainer}
          onClick={() => handleClickImage(info as InfoDocument)}
        >
          <div
            className={classNames(
              styles.boxInfo,
              !info.approved && selectedStyle[status],
            )}
            data-testid={`document-${info.image}`}
          >
            <FontAwesomeIcon
              icon={faFileLines}
              className={styles.iconDocument}
            />
            <span>{info.title}</span>
          </div>
          {info.image && (
            <span className={styles.textDocument}>Visualizar documento</span>
          )}
        </div>
      ))}

      {showModal && (
        <ModalShowDoc
          title={selectInfoDocument.title}
          imageUrl={selectInfoDocument.image}
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export { Documents };
